<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import FormSection from "@/Components/FormSection.vue";
import TextInput from "@/Components/TextInput.vue";
import NumberInput from "@/Components/NumberInput.vue";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import { computed, ref, watch, onMounted, watchEffect } from "vue";
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import SearchSelect from '@/Components/SearchSelect.vue'
import SelectInput from '@/Components/SelectInput.vue'

const props = defineProps({
  quote: Object,
  companies: Array,
  vendors: Array,
  clients: Array,
  paymentTerms: Array,
  employees: Array,
  taxes: Array,
  items: Array,
  shipViaOptions: Array,
  carriers: Array,
});

const showAddItemForm = ref(false);
const saving = ref(false);
const form = useForm({
  _method: 'POST',
  company_id: props.quote.company && props.quote.company.company_id ? props.quote.company.company_id : null,
  vendor_id: props.quote.vendor_id != null && props.quote.vendor_id !== ''
    ? Number(props.quote.vendor_id)
    : null,
  client_id: props.quote.client_id != null && props.quote.client_id !== ''
    ? Number(props.quote.client_id)
    : null,
  ship_to: props.quote.ship_to || null,
  ship_to_clientfield: props.quote.ship_to_clientfield || null,
  ship_to_customer_id: props.quote.ship_to_customer_id || null,
  ship_to_vendorfield: props.quote.ship_to_vendorfield || null,
  shipto_vendor_id: props.quote.shipto_vendor_id
  ? String(props.quote.shipto_vendor_id)
  : '',
  shipperno: props.quote.shipperno || null,
  other_name: props.quote.other_name || null,
  other_street: props.quote.other_street || null,
  other_city: props.quote.other_city || null,
  other_state: props.quote.other_state || null,
  other_zip: props.quote.other_zip || null,
  project_name: props.quote.project_name || null,
  po_number: props.quote.po_number || null,
  internal_po: props.quote.internal_po || null,
  po_date: props.quote.po_date ? dayjs(props.quote.po_date).format('YYYY-MM-DD') : null,
  good_thru: props.quote.good_thru ? dayjs(props.quote.good_thru).format('YYYY-MM-DD') : null,
  payment_id: props.quote.payment_id || null,
  sales_rep: props.quote.sales_rep || null,
  ship_via: props.quote.ship_via || null,
  carrier_id: props.quote.carrier_id || null,
  instruction_notes: props.quote.instruction_notes || null,
  items: Array.isArray(props.quote.items) ? props.quote.items.map((item) => ({ ...item })) : [],
  deleted_items: []
});

const itemsForm = ref({
  itemNumberId: null,
  description: null,
  unitPriceId: null,
  quantityId: null,
  type: null,
})

// Allow 0 values (e.g. 0% tax); only treat null/undefined/'' as missing.
const isFilled = (value) => value !== null && value !== undefined && value !== '';

const canAddItem = computed(() =>
  Boolean(itemsForm.value.itemNumberId) &&
  isFilled(itemsForm.value.unitPriceId) &&
  isFilled(itemsForm.value.quantityId) &&
  isFilled(itemsForm.value.type)
);

const activeDescriptionIndex = ref(null);

// Lock computed width to px so CSS horizontal resize works (ignored with %-based width).
const lockDescriptionWidth = (el) => {
  if (!el || el.dataset.widthLocked) {
    return;
  }
  requestAnimationFrame(() => {
    if (!el.isConnected || el.dataset.widthLocked) {
      return;
    }
    el.style.width = `${el.offsetWidth}px`;
    el.dataset.widthLocked = '1';
  });
};

watch(() => form.ship_to, (shipTo) => {
  if (shipTo == 1) {
    fillShipToAddress(form.client_id, 'client');
  } else if (shipTo == 2) {
    fillShipToAddress(form.vendor_id, 'vendor');
  }
}, { deep: true });

watch(() => form.client_id, () => {
  if (form.ship_to == 1) {
    fillShipToAddress(form.client_id, 'client');
  }
});

watch(() => form.vendor_id, () => {
  if (form.ship_to == 2) {
    fillShipToAddress(form.vendor_id, 'vendor');
  }
});

const itemTaxAmountId = computed(() => {
  return calculateItemTaxAmount(
    itemsForm.value.unitPriceId ?? 0,
    itemsForm.value.quantityId ?? 0,
    itemsForm.value.type ?? 0
  );
});

const itemAmountId = computed(() => {
  return calculateItemAmount(
    itemsForm.value.unitPriceId ?? 0,
    itemsForm.value.quantityId ?? 0
  );
});

const amountSubtotal = computed(() => {
  return form.items.reduce((n, { amount }) => n + amount, 0)
});

const taxSubtotal = computed(() => {
  return form.items.reduce((n, { tax_amount }) => n + tax_amount, 0)
});

const total = computed(() => {
  return amountSubtotal.value + taxSubtotal.value
});

onMounted(() => {
  if (!props.quote.qid) {
    setGoodThroughDate();
  }
});

const setGoodThroughDate = () => {
  let date = dayjs().add(61, 'day');
  if (date.day() == 6) {
    date = date.add(2, 'day');
  } else if (date.day() == 0) {
    date = date.add(1, 'day');
  }
  date = date.format('YYYY-MM-DD');
  form.good_thru = date;
}

const fillShipToAddress = (id, recipient = 'client') => {
  if (id) {
    const routeName = recipient == 'client' ? 'admin.companies.show' : 'admin.vendors.show';
    axios.get(route(routeName, { id: id }))
      .then(response => {
        const result = response.data;
        let shipTo = '';
        if (result.address) {
          shipTo += result.address;
        }
        if (result.address2) {
          shipTo += "\n" + result.address2;
        }
        if (result.city) {
          shipTo += "\n" + result.city;
        }
        if (result.state) {
          shipTo += "," + result.state;
        }
        if (result.zip) {
          shipTo += "\n" + result.zip;
        }
        if (recipient == 'client') {
          form.ship_to_clientfield = shipTo
          form.shipperno = result.shipperno
          form.ship_to_customer_id = result.clientID
        } else {
          form.ship_to_vendorfield = shipTo
          form.shipto_vendor_id = result.vendorID
            ? String(result.vendorID)
            : '';
        }
      })
      .catch(error => {
        console.log(error)
      });
  } else {
    alert(`Please select a ${recipient}`);
  }
}

const addItem = () => {
  if (!canAddItem.value) {
    return;
  }

  form.items.push({
    itemno: itemsForm.value.itemNumberId,
    description: itemsForm.value.description,
    unit_price: itemsForm.value.unitPriceId,
    quantity: itemsForm.value.quantityId,
    tax_type: itemsForm.value.type,
    tax_amount: itemTaxAmountId.value,
    amount: itemAmountId.value,
  });

  itemsForm.value = {
    itemNumberId: null,
    description: null,
    unitPriceId: null,
    quantityId: null,
    type: null,
  };
};

const syncItemsFromQuote = () => {
  const quoteItems = usePage().props.value.quote?.items ?? [];
  form.items = Array.isArray(quoteItems) ? quoteItems.map((item) => ({ ...item })) : [];
  form.deleted_items = [];
};

const deleteItem = (index) => {
  const item = form.items[index];
  form.items.splice(index, 1);
  if (item.item_id) {
    form.deleted_items.push(item.item_id);
  }
}

const handleItemInputChange = (index) => {
  form.items[index]['tax_amount'] = calculateItemTaxAmount(
    form.items[index]['unit_price'],
    form.items[index]['quantity'],
    form.items[index]['tax_type'],
  );
  form.items[index]['amount'] = calculateItemAmount(
    form.items[index]['unit_price'],
    form.items[index]['quantity'],
  );
}

const calculateItemTaxAmount = (unitPrice = 0, quantity = 0, taxType = 0) => {
  return Math.round(unitPrice * quantity * taxType * 100) / 100;
};

const calculateItemAmount = (unitPrice = 0, quantity = 0) => {
  return Math.round(unitPrice * quantity * 100) / 100;
};

const generateInternalQuoteRequest = () => {
  axios.get(route('admin.quotes.incrementInternalPoSequence'))
    .then(response => {
      form.internal_po = String(response.data)
    })
    .catch(error => {
      console.log('Sorry, Unable to process.Please try again later')
      console.log(error)
    });
}

const saveForm = () => {
  saving.value = true;
  let endpoint = route('admin.quotes.store');
  if (props.quote.qid) {
    endpoint = route('admin.quotes.update', { quote: props.quote.qid });
    form._method = 'PUT';
  }

  const element = document.getElementById('company_name')
  form.post(endpoint, {
    preserveScroll: true,
    onSuccess() {
      syncItemsFromQuote();
    },
    onError() {
      element.scrollIntoView();
    },
    onFinish() {
      saving.value = false;
    }
  })
};

const vendorOptions = computed(() =>
  (props.vendors || [])
    .filter((vendor) => vendor && (vendor.vendorID != null || vendor.id != null))
    .map((vendor) => ({
      label: vendor.vendorName ?? vendor.name ?? String(vendor.vendorID ?? vendor.id),
      value: Number(vendor.vendorID ?? vendor.id),
    }))
    .filter((option) => option.label && !Number.isNaN(option.value))
);

const vendorModel = computed({
  get: () => (
    form.vendor_id != null && form.vendor_id !== ''
      ? Number(form.vendor_id)
      : null
  ),
  set: (value) => {
    form.vendor_id = value != null ? Number(value) : null;
  },
});

const clientOptions = computed(() =>
  (props.clients || [])
    .filter((client) => client?.client && (client.ID != null || client.id != null))
    .map((client) => ({
      label: client.client,
      value: Number(client.ID ?? client.id),
    }))
    .filter((option) => option.label && !Number.isNaN(option.value))
);

const clientModel = computed({
  get: () => (
    form.client_id != null && form.client_id !== ''
      ? Number(form.client_id)
      : null
  ),
  set: (value) => {
    form.client_id = value != null ? Number(value) : null;
  },
});
</script>

<template>
  <AppLayout title="Edit Quote">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        <span v-if="quote.qid">Edit </span><span v-else>Create </span> Quote
      </h2>
    </template>

    <div class="max-w-7xl mx-auto py-5 sm:px-6 lg:px-8">
      <FormSection>
        <template #title>
          <a :href="route('admin.quotes.index')">
            <SecondaryButton>Back</SecondaryButton>
          </a>
        </template>
        <template #form>
          <!-- Company -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="company_name" value="Company" />
            <SelectInput name="company_name" id="company_name"
              class="mt-1"
              v-model="form.company_id">
              <option v-for="company in companies" :value="company.company_id" v-text="company.company"></option>
            </SelectInput>
            <InputError :message="form.errors.company" class="mt-2" />
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-3 col-span-6 sm:col-span-4">
            <!-- Vendor -->
            <div>
              <InputLabel for="vendor_id" value="Vendor" />
              <SearchSelect v-model="vendorModel" :options="vendorOptions" placeholder="Select vendor…"
                class="mt-1 w-full" :clearable="true" />
              <InputError :message="form.errors.vendor_id" class="mt-2" />
            </div>

            <!-- Client -->
            <div>
              <InputLabel for="client_id" value="Client" />
              <SearchSelect v-model="clientModel" :options="clientOptions" placeholder="Select client…"
                class="mt-1 w-full" :clearable="true" />
              <InputError :message="form.errors.client_id" class="mt-2" />
            </div>
          </div>

          <!-- Ship to -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel for="ship_to" value="Ship To" />
            <SelectInput name="ship_to_select"
              class="mt-1"
              v-model="form.ship_to">
              <option value="1">Client</option>
              <option value="2">Vendor</option>
              <option value="3">Other</option>
            </SelectInput>
            <InputError :message="form.errors.ship_to" class="mt-2" />
          </div>

          <!-- Ship to CLIENT -->
          <div class="col-span-6 sm:col-span-4 bg-gray-200 rounded-lg p-2" v-if="form.ship_to == 1">
            <div>
              <InputLabel for="client_shipto" value="Ship To" />
              <textarea name="client_shipto" id="client_shipto_id" v-model="form.ship_to_clientfield"
                class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full disabled:bg-gray-50"></textarea>
              <InputError :message="form.errors.ship_to_clientfield" class="mt-2" />
            </div>
            <div>
              <InputLabel for="client_customer_id" value="Client ID" />
              <TextInput id="client_customer_id" v-model="form.ship_to_customer_id" type="text"
                class="mt-1 block w-full" />
              <InputError :message="form.errors.ship_to_customer_id" class="mt-2" />
            </div>
          </div>

          <!-- Ship to VENDOR -->
          <div class="col-span-6 sm:col-span-4 bg-gray-200 rounded-lg p-2" v-if="form.ship_to == 2">
            <div>
              <InputLabel for="vendor_shipto" value="Ship To" />
              <textarea name="vendor_shipto" id="vendor_shipto_id" v-model="form.ship_to_vendorfield"
                class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full disabled:bg-gray-50"></textarea>
              <InputError :message="form.errors.ship_to_vendorfield" class="mt-2" />
            </div>
            <div>
              <InputLabel for="shipto_vendorId" value="Vendor ID" />
              <TextInput id="shipto_vendorId" v-model="form.shipto_vendor_id" type="text" class="mt-1 block w-full"
                @click="setGoodThroughDate" />
              <InputError :message="form.errors.shipto_vendor_id" class="mt-2" />
            </div>
          </div>

          <!-- Ship to OTHER -->
          <div class="col-span-6 sm:col-span-4 grid grid-cols-4 gap-4 bg-gray-200 rounded-lg p-2"
            v-if="form.ship_to == 3">
            <div class="col-span-2">
              <InputLabel for="other_name" value="Name" />
              <TextInput id="other_name" v-model="form.other_name" type="text" class="mt-1 block w-full" />
              <InputError :message="form.errors.other_name" class="mt-2" />
            </div>
            <div class="col-span-2">
              <InputLabel for="other_city" value="City" />
              <TextInput id="other_city" v-model="form.other_city" type="text" class="mt-1 block w-full" />
              <InputError :message="form.errors.other_city" class="mt-2" />
            </div>
            <div class="col-span-2">
              <InputLabel for="other_street" value="Street" />
              <TextInput id="other_street" v-model="form.other_street" type="text" class="mt-1 block w-full" />
              <InputError :message="form.errors.other_street" class="mt-2" />
            </div>
            <div>
              <InputLabel for="other_state" value="State" />
              <TextInput id="other_state" v-model="form.other_state" type="text" class="mt-1 block w-full" />
              <InputError :message="form.errors.other_state" class="mt-2" />
            </div>
            <div>
              <InputLabel for="other_zip" value="Zip" />
              <TextInput id="other_zip" v-model="form.other_zip" type="text" class="mt-1 block w-full" />
              <InputError :message="form.errors.other_zip" class="mt-2" />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-x-3 col-span-6 sm:col-span-4">
            <!-- Project name -->
            <div class="md:col-span-2">
              <InputLabel for="project_name" value="Project Name" />
              <TextInput id="project_name" v-model="form.project_name" type="text" class="mt-1 block w-full"
                autocomplete="project_name" />
              <InputError :message="form.errors.project_name" class="mt-2" />
            </div>

            <!-- Quote Number -->
            <div>
              <InputLabel for="po_number" value="Quote Number" :required="true" />
              <TextInput id="po_number" v-model="form.po_number" type="text" class="mt-1 block w-full" />
              <InputError :message="form.errors.po_number" class="mt-2" />
            </div>
          </div>

          <!-- Internal Quote Request -->
          <div class="col-span-6 sm:col-span-4 grid grid-cols-3">
            <div class="col-span-2">
              <InputLabel for="internal_po" value="Internal Quote Request" />
              <TextInput id="internal_po" v-model="form.internal_po" type="text" class="mt-1 block w-full" />
              <InputError :message="form.errors.internal_po" class="mt-2" />
            </div>
            <div class="flex items-end py-1">
              <SecondaryButton class="ml-2" @click.prevent="generateInternalQuoteRequest">
                Quote Request
              </SecondaryButton>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-3 col-span-6 sm:col-span-4">
            <!-- Quote Date -->
            <div>
              <InputLabel for="quote_date" value="Quote Date" />

              <Datepicker class="mt-1 block w-full" v-model="form.po_date" format="MMMM d, yyyy" model-type="yyyy-MM-dd"
                auto-apply :close-on-auto-apply="true" :enable-time-picker="false" />
              <InputError :message="form.errors.po_date" class="mt-2" />
            </div>

            <!-- Good Thru -->
            <div>
              <InputLabel for="good_thru" value="Good Thru" />

              <Datepicker class="mt-1 block w-full" v-model="form.good_thru" format="MMMM d, yyyy"
                model-type="yyyy-MM-dd" auto-apply :close-on-auto-apply="true" :enable-time-picker="false" />
              <InputError :message="form.errors.good_thru" class="mt-2" />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-3 col-span-6 sm:col-span-4">
            <!-- Payment Terms -->
            <div>
              <InputLabel for="vendor_id" value="Payment Terms" />
              <SelectInput name="vendor_id"
                class="mt-1"
                v-model="form.payment_id">
                <option v-for="paymentTerm in paymentTerms" :value="paymentTerm.payment_id"
                  v-text="paymentTerm.payment"></option>
              </SelectInput>
              <InputError :message="form.errors.payment_id" class="mt-2" />
            </div>

            <!-- Sales Rep -->
            <div>
              <InputLabel for="salesrep" value="Sales Rep" />
              <SelectInput name="salesrep"
                class="mt-1"
                v-model="form.sales_rep">
                <option v-for="employee in employees" :value="employee.employeeID">{{ employee.firstname }}
                  {{ employee.lastname }}</option>
              </SelectInput>
              <InputError :message="form.errors.sales_rep" class="mt-2" />
            </div>
          </div>
        </template>
      </FormSection>

      <FormSection :noGrid="true" class="mt-5" gridClasses="grid-cols-12">
        <template #title>
          Items
        </template>
        <template #form>
          <div class="col-span-12 mb-3">
            <SecondaryButton @click.prevent="showAddItemForm = !showAddItemForm">
              Add Item
            </SecondaryButton>
          </div>
          <div v-if="showAddItemForm" class="col-span-12 grid grid-cols-3 gap-4 bg-gray-200 rounded-lg p-2">
            <div class="col-span-1">
              <InputLabel for="item_number_id" value="Item Number" :required="true" />
              <TextInput id="item_number_id" v-model="itemsForm.itemNumberId" type="text" class="mt-1 block w-full" />
              <InputError :message="form.errors.itemNumberId" class="mt-2" />
            </div>
            <div class="col-span-2">
              <InputLabel for="description_id" value="Description" />
              <textarea name="desc" id="description_id" v-model="itemsForm.description" rows="2"
                class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block disabled:bg-gray-50 resize overflow-auto min-h-[2.5rem] min-w-full w-px max-w-none relative z-50 bg-white"></textarea>
              <InputError :message="form.errors.description" class="mt-2" />
            </div>
            <div>
              <InputLabel value="Unit Price" :required="true" />
              <TextInput v-model="itemsForm.unitPriceId" type="number" class="mt-1 block w-full" />
            </div>
            <div>
              <InputLabel value="Quantity" :required="true" />
              <NumberInput v-model="itemsForm.quantityId" step="1" class="mt-1 block w-full" />
            </div>
            <div>
              <InputLabel for="tax_type" value="Tax Type" :required="true" />
              <SelectInput name="tax_type"
                class="mt-1"
                v-model="itemsForm.type">
                <option v-for="tax in taxes" :value="tax.tax_amount" v-text="tax.tax_name"></option>
              </SelectInput>
              <InputError :message="form.errors.type" class="mt-2" />
            </div>
            <div class="flex flex-col space-y-3">
              <div class="flex space-x-2 items-center">
                <InputLabel for="tax_amount_id" value="Tax Amount:" />
                <span class="font-bold" v-text="itemTaxAmountId"></span>
              </div>
              <div class="flex space-x-2 items-center">
                <InputLabel for="amount_id" value="Amount:" />
                <span class="font-bold" v-text="itemAmountId"></span>
              </div>
            </div>
            <div class="col-span-2 flex items-center justify-end">
              <SecondaryButton @click.prevent="addItem"
                :disabled="!canAddItem">
                Add
              </SecondaryButton>
            </div>
          </div>
          <!-- Items -->
          <div class="col-span-12 grid grid-cols-2 md:grid-cols-3 gap-2 mt-3 overflow-visible isolate">
            <div v-for="(item, itemIndex) in form.items" :key="item.item_id ?? `new-${itemIndex}`"
              class="border border-gray-300 rounded-md p-2 space-y-1 overflow-visible relative min-w-0"
              :class="activeDescriptionIndex === itemIndex ? 'z-[100]' : 'z-0'">
              <div class="flex space-x-2 items-center">
                <span class="font-semibold text-sm w-full">Item Number</span>
                <TextInput v-model="item.itemno" class="w-full" padding="py-1 px-2.5" />
              </div>
              <div class="flex space-x-2 items-center">
                <span class="font-semibold text-sm w-full">Tax type</span>
                <SelectInput name="tax_type"
                  padding="py-1 px-2.5"
                  v-model="item.tax_type" @input="handleItemInputChange(itemIndex)">
                  <option v-for="tax in taxes" :value="tax.tax_amount" v-text="tax.tax_name"></option>
                </SelectInput>
              </div>
              <div class="flex space-x-2 items-center">
                <span class="font-semibold text-sm w-full">Unit Price</span>
                <TextInput v-model="item.unit_price" type="number" @input="handleItemInputChange(itemIndex)"
                  class="w-full" padding="py-1 px-2.5" />
              </div>
              <div class="flex space-x-2 items-center">
                <span class="font-semibold text-sm w-full">Quantity</span>
                <TextInput v-model="item.quantity" type="number" @input="handleItemInputChange(itemIndex)"
                  class="w-full" padding="py-1 px-2.5" />
              </div>
              <div class="flex space-x-2 items-center">
                <span class="font-semibold text-sm w-full">Tax Amount</span>
                <InputLabel :value="item.tax_amount" />
              </div>
              <div class="flex space-x-2 items-center">
                <span class="font-semibold text-sm w-full">Amount</span>
                <InputLabel :value="item.amount" />
              </div>
              <div class="flex space-x-2 items-start">
                <span class="font-semibold text-sm w-full pt-2">Description</span>
                <div class="w-full relative min-h-[2.5rem]">
                  <textarea v-model="item.description" rows="2"
                    :ref="lockDescriptionWidth"
                    @mousedown="activeDescriptionIndex = itemIndex"
                    @focus="activeDescriptionIndex = itemIndex"
                    class="absolute left-0 top-0 border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-lg block w-full py-1 px-2.5 disabled:bg-gray-50 resize overflow-auto min-h-[2.5rem] max-w-none bg-white"></textarea>
                </div>
              </div>
              <button class="fa fa-trash text-red-500" @click.prevent="deleteItem(itemIndex)"
                title="Delete item"></button>
            </div>
          </div>
          <!-- Amount Subtotal, Tax Subtotal and Total -->
          <div class="col-span-12 mt-5">
            <div class="flex flex-col space-y-3">
              <div class="flex space-x-2 items-center">
                <InputLabel value="AmountSubTotal:" />
                <span class="font-bold" v-text="amountSubtotal"></span>
              </div>
              <div class="flex space-x-2 items-center">
                <InputLabel value="TaxSubTotal:" />
                <span class="font-bold" v-text="taxSubtotal"></span>
              </div>
              <div class="flex space-x-2 items-center">
                <InputLabel value="Total:" />
                <span class="font-bold" v-text="total"></span>
              </div>
            </div>
          </div>
        </template>
      </FormSection>

      <FormSection class="mt-5">
        <template #form>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-3 col-span-6 sm:col-span-4">
            <!-- Ship Via -->
            <div>
              <InputLabel value="Ship Via" />
              <SelectInput
                class="mt-1"
                v-model="form.ship_via">
                <option v-for="shipViaOption in shipViaOptions" :value="shipViaOption.ship_via_id">
                  {{ shipViaOption.shipvia }}</option>
              </SelectInput>
              <InputError :message="form.errors.ship_via" class="mt-2" />
            </div>

            <!-- Shipper # -->
            <div>
              <InputLabel for="other_shipper_id" value="Shipper #" />
              <TextInput id="other_shipper_id" v-model="form.shipperno" type="text" class="mt-1 block w-full"
                :class="{ 'disabled': form.ship_to == 1 }" :disabled="form.ship_to == 1" />
              <InputError :message="form.errors.shipperno" class="mt-2" />
            </div>
          </div>

          <!-- Carrier -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Carrier" />
            <SelectInput
              class="mt-1"
              v-model="form.carrier_id">
              <option v-for="carrier in carriers" :value="carrier.carrier_id">{{ carrier.carrier }}</option>
            </SelectInput>
            <InputError :message="form.errors.carrier_id" class="mt-2" />
          </div>

          <!-- Instructions/Notes -->
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Instructions/Notes" />
            <textarea name="instructions" v-model="form.instruction_notes"
              class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full disabled:bg-gray-50"></textarea>
            <InputError :message="form.errors.instruction_notes" class="mt-2" />
          </div>
        </template>
        <template #actions>
          <a :href="route('admin.quotes.index')" class="mr-5" v-if="!saving">
            <SecondaryButton type="button">Back</SecondaryButton>
          </a>
          <PrimaryButton type="button" @click.prevent="saveForm" :disabled="saving">Save</PrimaryButton>
        </template>
      </FormSection>
    </div>
  </AppLayout>
</template>
